import { create } from 'zustand'
import { Client, UpdateClientDto } from '@/types'
import { clientsService } from '@/services/clients.service'

interface AttachedClientsState {
  clients: Client[] | null
  isInitialized: boolean
  fetchAttachedClients: (force?: boolean) => Promise<void>
  updateClient: (id: string, client: UpdateClientDto) => Promise<Client>
  enableClient: (id: string) => Promise<Client>
  disableClient: (id: string) => Promise<Client>
  blockClient: (id: string) => Promise<Client>
  unblockClient: (id: string) => Promise<Client>
  terminateClient: (id: string) => Promise<Client>
  deleteClient: (id: string) => Promise<void>
}

export const useAttachedClientsStore = create<AttachedClientsState>((set, get) => ({
  clients: null,
  isInitialized: false,

  fetchAttachedClients: async (force = false) => {
    if (get().isInitialized && !force) {
      return
    }

    try {
      const data = await clientsService.getAttached()
      set({ clients: data, isInitialized: true })
    } catch (error) {
      console.error('Error loading attached clients:', error)
      throw error
    }
  },

  updateClient: async (id: string, clientData: UpdateClientDto) => {
    const updatedClient = await clientsService.update(id, clientData)
    set(state => ({
      clients: state.clients?.map(client => (client._id === id ? updatedClient : client)),
    }))
    return updatedClient
  },

  enableClient: async (id: string) => {
    const updatedClient = await clientsService.enable(id)
    set(state => ({
      clients: state.clients?.map(client => (client._id === id ? updatedClient : client)),
    }))
    return updatedClient
  },

  disableClient: async (id: string) => {
    const updatedClient = await clientsService.disable(id)
    set(state => ({
      clients: state.clients?.map(client => (client._id === id ? updatedClient : client)),
    }))
    return updatedClient
  },

  blockClient: async (id: string) => {
    const updatedClient = await clientsService.block(id)
    set(state => ({
      clients: state.clients?.map(client => (client._id === id ? updatedClient : client)),
    }))
    return updatedClient
  },

  unblockClient: async (id: string) => {
    const updatedClient = await clientsService.unblock(id)
    set(state => ({
      clients: state.clients?.map(client => (client._id === id ? updatedClient : client)),
    }))
    return updatedClient
  },

  terminateClient: async (id: string) => {
    const updatedClient = await clientsService.terminate(id)
    set(state => ({
      clients: state.clients?.map(client => (client._id === id ? updatedClient : client)),
    }))
    return updatedClient
  },

  deleteClient: async (id: string) => {
    await clientsService.delete(id)
    set(state => ({
      clients: state.clients?.filter(client => client._id !== id),
    }))
  },
}))
